// @ts-nocheck
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface MyOrgMembership {
  id: string;
  name: string;
  logo_url: string | null;
  role: string | null;
}

interface MyOrganizationsContextType {
  organizations: MyOrgMembership[];
  activeOrganization: MyOrgMembership | null;
  activeOrgId: string | null;
  setActiveOrgId: (id: string) => void;
  isLoading: boolean;
}

const MyOrganizationsContext = createContext<MyOrganizationsContextType | null>(
  null
);

const storageKey = (userId: string) => `rle.activeOrgId.${userId}`;

export const MyOrganizationsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<MyOrgMembership[]>([]);
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setActiveOrgId = useCallback(
    (id: string) => {
      setActiveOrgIdState(id);
      if (user?.id) {
        try {
          localStorage.setItem(storageKey(user.id), id);
        } catch {}
      }
    },
    [user?.id]
  );

  useEffect(() => {
    if (!user?.id) {
      setOrganizations([]);
      setActiveOrgIdState(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const { data } = await (supabase as any)
        .from("organization_members")
        .select("organization_id, role, organizations:organization_id(id, name, logo_url)")
        .eq("user_id", user.id)
        .eq("status", "active");

      if (cancelled) return;

      const orgs: MyOrgMembership[] = (data || [])
        .map((m: any) =>
          m.organizations
            ? {
                id: m.organizations.id,
                name: m.organizations.name,
                logo_url: m.organizations.logo_url,
                role: m.role ?? null,
              }
            : null
        )
        .filter(Boolean);

      setOrganizations(orgs);

      // Restore a previously selected org if it's still a valid membership.
      let stored: string | null = null;
      try {
        stored = localStorage.getItem(storageKey(user.id));
      } catch {}
      const valid =
        stored && orgs.some((o) => o.id === stored) ? stored : orgs[0]?.id ?? null;
      setActiveOrgIdState(valid);
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const activeOrganization =
    organizations.find((o) => o.id === activeOrgId) ?? null;

  return (
    <MyOrganizationsContext.Provider
      value={{
        organizations,
        activeOrganization,
        activeOrgId,
        setActiveOrgId,
        isLoading,
      }}
    >
      {children}
    </MyOrganizationsContext.Provider>
  );
};

export const useMyOrganizations = () => useContext(MyOrganizationsContext);
